import { StandardGroupingGrid, StandardGroupingGridPresentationType } from '@apply/components';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { formatDecimalAsTwoDigitPercentage } from './functions/formatters/formatDecimalAsTwoDigitPercentage.tsx';
import { formatBoolAsYN } from './functions/formatters/formatBoolAsYN.tsx';
import { BridgeAppraisalStatusGroupMapper } from './functions/mappers/bridgeAppraisalStatusGroupMapper.tsx';
import { renderAppraisedCell } from './functions/renderers/renderAppraisedCell.tsx';
import { BridgeApplication } from '../../mockData/mockApplicationsGridData.ts';

export interface ApplicationsGridProps {
    data: BridgeApplication[];
}

export const ApplicationsGrid = ({ data }: ApplicationsGridProps) => {
    return (
        <>
            <StandardGroupingGrid
                dataSource={data}
                keyExpr='applicationId'
                presentationType={StandardGroupingGridPresentationType.Enhanced}
                noDataText='No Applications Found'
                pagerVisible={false}
                groupPanelVisible={false}
                rowAlternationEnabled={true}
                allowCollapsing={false}
                showColumnHeaders={true}
                showBorders={true}
                remoteOperations={false}
                autoExpandAll={true}
            >
                <Column
                    dataField='appraisalStatusTypeId'
                    groupIndex={0}
                    calculateGroupValue={(rowData) => {
                        return BridgeAppraisalStatusGroupMapper.getGroupSection(rowData.appraisalStatusTypeId);
                    }}
                    sortOrder='asc'
                    groupCellRender={(cellInfo) => {
                        const label = BridgeAppraisalStatusGroupMapper.getGroupLabel(cellInfo.value);
                        return <i>{label}</i>;
                    }}
                    fixed={true}
                />

                <Column
                    dataField='loan'
                    caption='Loan'
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField='borrower'
                    caption='Borrower'
                    width={200}
                    alignment='left'
                />
                <Column
                    dataField='city'
                    caption='City'
                    width={120}
                    alignment='left'
                />
                <Column
                    dataField='state'
                    caption='State'
                    width={50}
                    alignment='left'
                />
                <Column
                    dataField='units'
                    caption='Units'
                    width={50}
                    alignment='left'
                />
                <Column
                    dataField='estimatedSize'
                    caption='Est. Size'
                    width={100}
                    alignment='left'
                    format={'currency'}
                />
                <Column
                    dataField='estimatedRate'
                    caption='Est. Rate'
                    width={80}
                    alignment='left'
                    customizeText={(e) => formatDecimalAsTwoDigitPercentage(e.value)}
                />
                <Column
                    dataField='term'
                    caption='Term'
                    width={80}
                    alignment='left'
                />
                <Column
                    dataField='ltv'
                    caption='LTV'
                    width={60}
                    alignment='left'
                    customizeText={(e) => formatDecimalAsTwoDigitPercentage(e.value)}
                />
                <Column
                    dataField='items'
                    caption='Items'
                    width={50}
                    alignment='left'
                    dataType='string'
                    customizeText={(e) => formatBoolAsYN(e.value)}
                />
                <Column
                    dataField='appraised' // Handle styles at the column level (bg color)
                    caption='Appraised'
                    width={100}
                    alignment='left'
                    cellRender={(cellInfo) =>
                        renderAppraisedCell({
                            status: cellInfo.data.appraisalStatusTypeId,
                            propertyState: cellInfo.data.state,
                            appraisalAmount: cellInfo.data.appraisalAmount,
                            appraisalReceivedDate: cellInfo.data.appraisalReceivedDate,
                        })
                    }
                />
                <Column
                    dataField='credit'
                    caption='Credit' // To be removed potentially pending reqs
                    width={80}
                    alignment='left'
                />
                <Column
                    dataField='experience'
                    caption='Experience'
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField='appliedDate'
                    caption='Apply'
                    width={100}
                    alignment='left'
                    dataType='date'
                    format='M/dd/yyyy'
                />
                <Column
                    dataField='bfiRefi'
                    caption='Refi'
                    width={60}
                    alignment='left'
                    dataType='string'
                    customizeText={(e) => formatBoolAsYN(e.value)}
                />
                <Column
                    dataField='broker'
                    caption='Broker'
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField='note' // To be renamed potentially pending reqs
                    caption='Note'
                    width={200}
                    alignment='left'
                />
                <Column
                    dataField='primaryMotivation'
                    caption='Primary Motivation'
                    width={150}
                    alignment='left'
                />
                <Column
                    dataField='poc'
                    caption='POC'
                    alignment='left'
                    width={60}
                />
                <Summary
                    calculateCustomSummary={(options) => {
                        if (options.name === 'bfiRefi') {
                            if (options.summaryProcess === 'start') {
                                options.totalValue = 0;
                            }

                            if (options.summaryProcess === 'calculate') {
                                if (options.value === true) {
                                    options.totalValue += 1;
                                }
                            }
                        }
                        if (options.name === 'avgEstimatedRate') {
                            if (options.summaryProcess === 'start') {
                                options.totalValue = { sum: 0, count: 0 };
                            }

                            if (options.summaryProcess === 'calculate') {
                                const rate = options.value;
                                if (rate != null) {
                                    options.totalValue.sum += rate;
                                    options.totalValue.count += 1;
                                }
                            }

                            if (options.summaryProcess === 'finalize') {
                                const avg = options.totalValue.sum / options.totalValue.count;
                                options.totalValue = avg;
                            }
                        }
                    }}
                >
                    <TotalItem
                        column='loan'
                        summaryType='count'
                        showInColumn='loan'
                        displayFormat='Total / Avg {0}'
                    />
                    <TotalItem
                        column='units'
                        summaryType='sum'
                        showInColumn='units'
                        displayFormat='{0}'
                    />
                    <TotalItem
                        name='estimatedSize'
                        column='estimatedSize'
                        summaryType='sum'
                        showInColumn='estimatedSize'
                        displayFormat='{0}'
                        valueFormat={'currency'}
                    />
                    <TotalItem
                        name='avgEstimatedRate'
                        column='estimatedRate'
                        summaryType='custom'
                        showInColumn='estimatedRate'
                        displayFormat='{0}'
                        customizeText={(e) => formatDecimalAsTwoDigitPercentage(e.value)}
                    />
                    <TotalItem
                        name='bfiRefi'
                        column='bfiRefi'
                        showInColumn='bfiRefi'
                        displayFormat='{0}'
                        summaryType='custom'
                    />
                </Summary>
            </StandardGroupingGrid>
        </>
    );
};
